/**
 * The choice a link dialog offers (HW-983): linking a board to a project gives
 * nobody access, so the dialog lists the project's people and the ticked ones
 * become members of the board.
 */

type ProjectPerson = { employeeId: string; removedAt: string | null; isCurrentEmployee?: boolean };

/**
 * The project's current people split in two: those the dialog offers to add,
 * and those already on the board (shown ticked, not offered — the server
 * leaves their role as it is anyway). Somebody who left the project, or no
 * longer works here (`isCurrentEmployee: false`), is in neither: the server
 * refuses to add them.
 */
export function splitProjectPeople<T extends ProjectPerson>(
  members: readonly T[] | undefined,
  onBoard: ReadonlySet<string>
): { offered: T[]; alreadyOnBoard: T[] } {
  const current = (members ?? []).filter(
    (m) => m.removedAt === null && m.isCurrentEmployee !== false
  );
  return {
    offered: current.filter((m) => !onBoard.has(m.employeeId)),
    alreadyOnBoard: current.filter((m) => onBoard.has(m.employeeId)),
  };
}

/** The "Select all" box: ticked when every offered person is, a dash when some are. */
export function selectAllState(
  offeredIds: readonly string[],
  selected: readonly string[]
): boolean | 'indeterminate' {
  const chosen = offeredIds.filter((id) => selected.includes(id)).length;
  if (offeredIds.length === 0 || chosen === 0) return false;
  return chosen === offeredIds.length ? true : 'indeterminate';
}

/** "Select all" pressed: everybody offered, or nobody once all are ticked already. */
export function toggleAll(offeredIds: readonly string[], selected: readonly string[]): string[] {
  return selectAllState(offeredIds, selected) === true ? [] : [...offeredIds];
}

/** One person's box pressed. */
export function togglePerson(selected: readonly string[], employeeId: string): string[] {
  return selected.includes(employeeId)
    ? selected.filter((id) => id !== employeeId)
    : [...selected, employeeId];
}
